/// <amd-module name="TFS/Admin/Groups/Dialogs/GroupAddPanel/TeamAddSection" />
import * as React from "react";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { IdentityPicker, IIdentity, IPeoplePickerProvider, IdentityPickerDropdown } from "VSSUI/IdentityPicker";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssPageContext } from "VSS/Platform/Context";
import { IEntityType, TeamsMinAdminNumber } from "TFS/Admin/Groups/Common/Constants";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Checkbox } from "VSSUI/Checkbox";
import { IVssPageService } from "VSS/Platform/Page";
import { Observer } from "VSSUI/Observer";
import { TeamsPermissionsPickerProvider } from "TFS/Admin/Groups/Dialogs/GroupAddPanel/TeamsPermissionsPickerProvider";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { FormItem } from "VSSUI/FormItem";

interface TeamAddSectionProps {
    pageContext: IVssPageContext;
    adminSelectedIdentities: ObservableArray<IIdentity>;
    onAdminAdded: (identity: IIdentity) => void;
    onAdminsRemoved: (identities: IIdentity[]) => void;
    onAdminRemoved: (identity: IIdentity) => void;
    setAreaPath: ObservableValue<boolean>;
    onAreaPathCheckChanged: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => void;
    adminAsMember: ObservableValue<boolean>;
    onAdminAsMemberChanged:(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => void;
    permissionsGroups: IIdentity[];
    parentSelectedIdentity: ObservableValue<IIdentity | undefined>;
}

interface TeamAddSectionState {}

export class TeamAddSection extends React.Component<TeamAddSectionProps, TeamAddSectionState> {
    private _adminPickerProvider: IPeoplePickerProvider;

    constructor(props: TeamAddSectionProps) {
        super(props);

        this._adminPickerProvider = new PeoplePickerProvider(this.props.pageContext, {
            options: {
                MinResults: 5,
                MaxResults: 20
            },
            identityTypes: [IEntityType.User, IEntityType.Group]
        });

        const vssPageService = this.props.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (vssPageData) {
            const { user } = vssPageData;
            let identity = {
                localId: user.id,
                originId: user.id,
                displayName: user.displayName,
                image: getIdentityImageUrl(this.props.pageContext, user),
                subjectDescriptor: user.descriptor,
                entityType: IEntityType.User,
                mail: user.uniqueName
            } as IIdentity;
            this.props.adminSelectedIdentities.push(identity);
        }
    }

    public render(): JSX.Element {
        const { setAreaPath, onAreaPathCheckChanged, adminAsMember, onAdminAsMemberChanged } = this.props;
        return (
            <div className="flex-column rhythm-vertical-20">
                <div className="flex-column no-margin">
                    <FormItem
                        className="group-add-item"
                        label={Resources.AdministratorsLabel}
                        message={this._getTeamsAdminError()}
                        error={this.props.adminSelectedIdentities.length < TeamsMinAdminNumber}
                    >
                        <IdentityPicker
                            pickerProvider={this._adminPickerProvider}
                            onIdentityAdded={this.props.onAdminAdded}
                            onIdentitiesRemoved={this.props.onAdminsRemoved}
                            onIdentityRemoved={this.props.onAdminRemoved}
                            selectedIdentities={this.props.adminSelectedIdentities}
                            placeholderText={Resources.AdministratorsIdentityPickerPlaceholder}
                        />
                    </FormItem>
                    <div className="secondary-text">{Resources.TeamAddAdministratorsExplanatoryLabel}</div>
                </div>
                <Checkbox onChange={onAdminAsMemberChanged} checked={adminAsMember} label={Resources.AdminMemberLabel} />
                <div className="flex-column">
                    <FormItem className="group-add-item" label={Resources.PermissionsLabel}>
                        <IdentityPickerDropdown
                            onChange={(identity?: IIdentity) => {
                                this.props.parentSelectedIdentity.value = identity;
                            }}
                            pickerProvider={new TeamsPermissionsPickerProvider(this.props.permissionsGroups)}
                            value={this.props.parentSelectedIdentity}
                        />
                    </FormItem>
                    <div className="secondary-text">{Resources.TeamAddParentsExplanatoryLabel}</div>
                </div>
                <Checkbox onChange={onAreaPathCheckChanged} checked={setAreaPath} label={Resources.AreaPathLabel} />
            </div>
        );
    }

    private _getTeamsAdminError() {
        return this.props.adminSelectedIdentities.length < TeamsMinAdminNumber ? Resources.TeamsAdminsError : "";
    }
}
